<template>
  <form @submit.prevent="submit" class="p-t-15" role="form" novalidate="novalidate">
    <div>
      <div class="col-lg-8 form-position-center">
        <server-errors />

        <div class="mt-0 mb-3 col-12 validating-manager" v-if="displayCreatorManager">
          <span>{{ $t('UserForm.added_by')  }} : {{ user.adding_manager.name }} </span>
        </div>

        <div class="mt-0 mb-3 col-12 validating-manager" v-if="displayValidatingManager">
          <span>{{ $t('UserForm.validated_by')  }} : {{ user.validating_manager.name }}</span>
        </div>

        <personnal-informations-fields v-bind:personnal-informations="personnalInformations"
                                       v-bind:user-exist="userExist"
                                       v-bind:existence-in-entity="existenceInEntity"
                                       v-bind:user-exist-different-role="userExistDifferentRole"
                                       v-bind:is-manager="isManager"
                                       @change="onChange($event.field, $event.value)"
                                       @update-special-char="specialChar" />


        <template v-if="currentEntity && entityCustomFields">
          <entity-custom-fields :key="entityCustomFieldsKey " v-bind:custom-fields="entityCustomFields"
                                v-bind:page-type="pageType"
                                @user-custom-fields="setUserCustomFields"
                                />
        </template>

      
        <div v-if="showEntityName" class="form-group form-group-default required">
          <label>{{ $t("UserForm.establishment") }}</label>
          <input :value="entityName"
                 name="entity"
                 readonly
                 type="text"
                 class="form-control text-black"
                 required="">
        </div>

        <template v-if="!isMultipleEdit">
          <profession-form :data-user="userToAddon"
                           :current-user="currentUser"
                           :availableServices="servicesOptions"
                           :availableHubs="hubsOptions"
                           :availableOccupations="occupationsOptions"
                           :is-manager="isManager"
                           :collapseHubs="collapseHubs"
                           @change="onProfessionFormChange" />
        </template>

        <button
          v-if="showAddOn && !showEntityTable"
          type="button"
          class="btn add-establishment"
          @click="showCandidateAddonPopup"
          :disabled="userExistDifferentRole || existenceInEntity">
          {{ $t("UserForm.add_to_other_establishments") }}
        </button>
      </div>

      <div v-if="showEntityTable" :class="classTableEntity">
        <candidate-entities-table v-if="!isManager"
                                  :candidateEntities="otherUserEntities" />
        <manager-entities-table :managerEntities="otherUserEntities" v-if="isManager" />

        <button
          v-if="showAddOn"
          type="button"
          class="btn update-establishment"
          @click="showCandidateAddonPopup"
          :disabled="userExistDifferentRole || existenceInEntity">
          {{ $t("UserForm.modify_button") }}
        </button>
      </div>

      <professional-info-table v-if="isMultipleEdit" :isManager="isManager" />
    </div>

    <div class="row" v-if="canEditContract && !isInternal">
      <div class="col-lg-6 mt-3 form-position-center">
        <h4 class="table-title collapsible" @click="toggleContractFields" ref="collapsible">
          <span>{{ $t('UserForm.contract_title') }}</span>
        </h4>

        <transition name="slide-fade">
          <div v-show="showContractField">
            <div class="alert contract-information">
              <p>{{ $t('UserForm.contract_message') }}</p>
            </div>

            <contract-fields :value="user"
                             @change="onChange($event.field, $event.value)" />
          </div>
        </transition>
      </div>
    </div>

    <div class="d-flex justify-content-center p-t-20">
      <router-link :to="{ name: this.isManager ? 'Managers' : 'Users' }" class="btn btn-default btn-cons m-t-10">
        {{ $t("UserForm.cancel") }}
      </router-link>
      <button
        type="submit"
        :disabled="disableSubmitButton"
        class="btn btn-primary btn-cons m-t-10">
          <i class="fa fa-spinner fa-spin" v-if="submitLoading"></i>
          {{ buttonName }}
      </button>
      <button
        v-if="showValidateButton"
        type="button"
        class="btn btn-primary btn-cons m-t-10 validate"
        :disabled="disableSubmitButton"
        @click="submitAndActivateUser">
        <i class="fa fa-spinner fa-spin" v-if="activateLoading"></i>
        {{ $t('buttons.modif_and_validate') }}
      </button>
    </div>

    <candidate-addon-popup
      v-if="showAddOn"
      :key="addOnPopupKey"
      :entityId="entityId"
      :occupations="user.occupations"
      :email="user.email"
      :isManager="isManager"
    />
  </form>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { roles } from '@/settings/roles'
import { roleAdmins, roleManagers } from '@/settings/roles'
import { formatNumber, parseNumber } from 'libphonenumber-js'
import CandidateAddonPopup from './CandidateAddonPopup'
import ManagerEntitiesTable from './ManagerEntitiesTable'
import CandidateEntitiesTable from './CandidateEntitiesTable'
import ProfessionalInfoTable from './ProfessionalInfoTable'
import { checkCanEditContract } from '@/helpers/contracts'
import { getCorporationUnavailabilityDatesProviderId } from '@/backend/users'
import AffiliatedServiceField from '@/components/users/form/AffiliatedServiceField'
import ServerErrors from './form/ServerErrors'
import PersonnalInformationsFields from './form/PersonnalInformationsFields'
import EntityCustomFields from '@/components/form/EntityCustomFields'
import ServicesField from './form/ServicesField'
import OccupationsField from './form/OccupationsField'
import ResourceTypeField from './form/ResourceTypeField'
import InputText from './form/InputText'
import ContractFields from './form/ContractFields'
import ProfessionForm from '@/components/candidate/ProfessionForm'
import isEmail from 'validator/lib/isEmail';
import AtInternetManagingCandidate from '@/helpers/at_internet/managing_candidate'
import AtInternetManagingManager from '@/helpers/at_internet/managing_manager'

import _ from 'lodash'

export default {
  name: 'UserForm',
  props: {
    userId: Number,
    entityId: Number,
    isManager: { type: Boolean, default: false },
    collapseHubs: { type: Boolean, default: false },
  },
  components: {
    CandidateAddonPopup,
    ManagerEntitiesTable,
    CandidateEntitiesTable,
    ProfessionalInfoTable,
    AffiliatedServiceField,
    ServerErrors,
    PersonnalInformationsFields,
    EntityCustomFields,
    ServicesField,
    OccupationsField,
    ResourceTypeField,
    InputText,
    ContractFields,
    ProfessionForm
  },
  data() {
    return {
      userExist: false,
      existenceInEntity: false,
      userExistDifferentRole: false,
      resetInfoOnEmailChange: false,
      otherUserEntities: [],
      civilityOptions: [this.$i18n.t("models.user.mrs"), this.$i18n.t("models.user.mr")],
      submitLoading: false,
      activateLoading: false,
      showContractField: false,
      canRender: false,
      servicesOptions: [],
      occupationsOptions: [],
      hubsOptions: [],
      specialCharError: false,
      entityCustomFields: false,
      entityCustomFieldsKey: ''
    }
  },
  computed: {
    ...mapGetters({
      addOns: 'users/addOns',
      addOnPopupKey: 'users/addOnPopupKey',
      addOnsChange: 'users/addOnsChange',
      user: 'users/user',
      currentUser: 'auth/currentUser',
      manageableServicesOptions: 'users/manageableServices',
      manageableOccupationsOptions: 'users/manageableOccupations',
      manageableHubsOptions: 'users/manageableHubs',
      manageableEntitiesOptions: 'users/manageableEntities',
      hasErrors: 'users/hasErrors',
      serverErrors: 'users/errors'
    }),
    listPath(){
      return this.isManager ? 'Managers' : 'Users';
    },
    actionName(){
      return this.isNew ? 'users/create' : 'users/update';
    },
    affiliatedServiceValidationRules(){
      return this.isInternal ? 'required' : ''
    },
    isInternal(){
      return this.user.resource_type == 'intern'
    },
    buttonName(){
      return this.isNew ? this.$t("actions.add") : this.$t("actions.update")
    },
    entityList() {
      return this.currentUser.entities
    },
    currentEntity() {
      return this.entityList ? this.entityList.find(entity => entity.id == this.entityId) : null
    },
    entityName() {
      return this.currentEntity ? this.currentEntity.name : ''
    },
    showEntityName() {
      return (
        this.entityList &&
        this.entityList.length > 1 &&
        !this.isMultipleEdit
      );
    },
    showAddOn() {
      if (this.isEditPage) return false

      if (this.currentEntity && (this.currentEntity.independent || this.currentEntity.single)) {
        return false
      } else {
        return roleManagers.includes(this.currentUser.role) && this.entityList.length > 0
      }
    },
    showEntityTable() {
      return this.otherUserEntities.length > 0
    },
    classTableEntity() {
      return this.isManager ? 'col-lg-8 form-position-center p-t-20 p-l-0' : 'col-md-10 offset-md-1 p-t-20'
    },
    canEditContract() {
      return checkCanEditContract(this.currentUser, this.entityId)
    },
    isEditPage() {
      return !this.isNew
    },
    isMultipleEdit() {
      return this.isEditPage && this.addOns.length > 1
    },
    showValidateButton() {
      return this.isEditPage && roleAdmins.includes(this.currentUser.role) && this.addOns.find(addOn => addOn.aasm_state == 'confirmed')
    },
    displayValidatingManager() {
      if (typeof this.user.validating_manager == 'undefined') return false
      if (this.user.validating_manager.name == null) return false
      return this.isEditPage && !this.isManager
    },
    displayCreatorManager() {
      if (typeof this.user.adding_manager == 'undefined') return false
      if (this.user.adding_manager.name == null) return false
      return this.isEditPage && !this.isManager
    },
    isNew() {
      return this.userId == null
    },
    disableSubmitButton() {
      return (
        this.userExistDifferentRole ||
        this.existenceInEntity ||
        this.submitLoading ||
        this.activateLoading ||
        this.specialCharError
      );
    },
    cannotEditService(){
      return this.currentUser.role == roles.recruiter && this.isEditPage
    },
    personnalInformations() {
      return {
        email: this.user.email,
        last_name: this.user.last_name,
        first_name: this.user.first_name,
        phone_display: this.user.phone_display
      }
    },
    pageType() {
      return this.isManager ? 'manager' : 'candidate';
    },
    userToAddon() {
      return {
        entity_id: this.entityId,
        entity: this.manageableEntitiesOptions.find(entity => entity.id == this.entityId),
        unavailability_dates_provider_id: this.user.unavailability_dates_provider_id,
        resource_type: this.user.resource_type,
        affiliated_service: this.user.affiliated_service,
        services: this.user.services,
        occupations: this.user.occupations,
        function: this.user.function
      }
    }
  },
  watch: {
    'user.email': function(value) {
      this.resetUserChecks()
      if (!this.isEditPage && isEmail(this.user.email)) {
        this.$store.dispatch('users/checkUserExistenceInEntity', { email: this.user.email, entity_id: this.entityId })
        .then((response) => {
          if (response) {
            this.userExistDifferentRole = this.isManager && response.role != "recruiter" || !this.isManager && response.role != "candidate"
            this.userExist = true
            this.existenceInEntity = response.existence
            if (!this.userExistDifferentRole && !this.existenceInEntity) {
              if (!this.isManager && this.currentEntity.unavailability_dates_provider) {
                this.prefillUnavailabityDateProviderId(response.id)
              }
              this.user.id = response.id
              this.user.first_name = response.first_name
              this.user.last_name = response.last_name
              this.user.phone_display = formatNumber(parseNumber(response.phone, 'FR'), 'E.164')
              this.resetInfoOnEmailChange = true
            }
          } else if (this.resetInfoOnEmailChange) {
            this.resetUserInfo();
          }
        })

        this.verifyEmail(this.user.email)
      }
    },
    currentUser() {
      this.setCustomFields()
    },  
    currentEntity() {
      this.setCustomFields()
    },
    addOnsChange() {
      this.otherUserEntities = this.addOns.filter(addOn => addOn.selected)
    }
  },
  methods: {
    ...mapActions({
      getUser: 'users/get',
      resetUser: 'users/reset',
      updateAndActivate: 'users/updateAndActivate',
      verifyEmail: 'users/verifyEmail'
    }),
    setUserCustomFields(custom_fields){
      this.user.custom_fields = custom_fields
    },
    onChange(fieldName, value) {
      this.user[fieldName] = value
    },
    specialChar(value){
      this.specialCharError = value
    },
    onProfessionFormChange(value) {
      this.user.resource_type = value.resource_type
      this.user.function = value.function
      this.user.affiliated_service = value.affiliated_service
      this.user.services = value.services
      this.user.occupations = value.occupations
      this.user.unavailability_dates_provider_id = value.unavailability_dates_provider_id
    },
    prefillUnavailabityDateProviderId(userId) {
      getCorporationUnavailabilityDatesProviderId(userId, this.entityId)
        .then(response => {
          this.user.unavailability_dates_provider_id = response.data.unavailability_dates_provider_id
        })
        .catch(error => console.error(error))
    },
    prepareUserForSave(){
      this.user.phone = formatNumber(parseNumber(this.user.phone_display, 'FR'), 'E.164')
      this.user.birthdate = this.user.birthdate_display
      this.user.entity_id = this.entityId

      if (this.isEditPage) {
        if (this.addOns.length > 1) {
          this.user.add_ons = [...this.addOns]
        } else {
          this.user.add_ons = [{
            entity_id: this.user.entity_id,
            unavailability_dates_provider_id: this.user.unavailability_dates_provider_id,
            resource_type: this.user.resource_type,
            function: this.user.function,
            affiliated_service: this.user.affiliated_service,
            services: this.user.services,
            occupations: this.user.occupations,
          }]
        }
      } else {
        const validAddons = this.addOns.filter(addon => addon.selected)
        if (validAddons.length > 0) {
          this.user.add_ons = validAddons
        }
      }
    },
    submit(){
      this.$validator.validateAll().then((result) => {
        if(result){
          this.submitLoading = true;
          this.prepareUserForSave()
          const listPath = this.listPath;
          const userName = this.user.first_name + ' ' + this.user.last_name

          this.$store.dispatch(this.actionName, this.user).then(() => {
            if(!this.hasErrors){
              if(!this.$route.path.includes("edit")){
                this.$route.path.includes("users") ?
                  AtInternetManagingCandidate.sendAddCandidatEvent() :
                  AtInternetManagingManager.sendAddManagerEvent()
              }
              let message = this.isEditPage ? 'messages.user_update_success' : 'messages.user_create_success'
              this.$store.commit('users/SET_ENTITY_ID', this.entityId)
              let state = 'pending'
              if (this.isEditPage) {
                state = this.addOns.find(addOn => addOn.entity.id == this.entityId).aasm_state
              }
              this.$store.commit('users/SET_ACTIVE_TAB', state)
              this.resetForm()
              this.redirectToListPath(listPath)
              this.$store.dispatch('message/enable', {
                type: 'success',
                content: this.$t(message, { 'name' : userName })
              })
            }
            this.submitLoading = false
          }).catch(() => this.submitLoading = false)
        }
      });
    },
    submitAndActivateUser() {
      this.$validator.validateAll().then((result) => {
        if(result){
          this.activateLoading = true;
          this.prepareUserForSave()

          const payload = {
            user: this.user,
            entityId: this.entityId
          }
          this.updateAndActivate(payload)
            .then(() => this.activateLoading = false)
            .catch(() => this.activateLoading = false)
        }
      });
    },

    resetForm(){
      this.$validator.reset();
    },
    redirectToListPath(listPath){
      this.$router.push({ name: listPath, params: { entity_id: this.entityId }});
    },
    resetUserChecks(){
      this.userExist = false
      this.existenceInEntity = false
      this.userExistDifferentRole = false
    },
    resetUserInfo() {
      this.resetUserChecks();
      this.$store.commit('users/SET_ADDON_POPUP_KEY')
      this.resetInfoOnEmailChange = false;
    },
    showCandidateAddonPopup() {
      $('#candidate-addon-popup').modal('show')
    },
    toggleContractFields() {
      this.showContractField = !this.showContractField
      this.$refs.collapsible.classList.toggle('opened')
    },
    setCustomFields(){

      this.entityCustomFields = {
        configurations: this.currentEntity.entity_custom_fields,
        user: this.userId ? this.user.custom_fields : {}
      }
      
      if(this.entityCustomFieldsKey == JSON.stringify(this.entityCustomFields)){
        return;
      }

      this.entityCustomFieldsKey = JSON.stringify(this.entityCustomFields)
    }
  },
  mounted: async function() {
    this.resetUser()
    await this.$store.dispatch('users/initManageableOptions', { entityId: this.entityId });
    this.servicesOptions = this.manageableServicesOptions
    this.hubsOptions = this.manageableHubsOptions
    this.occupationsOptions = this.manageableOccupationsOptions
    if (this.userId) {
      await this.getUser({ userId: this.userId, entityId: this.entityId });
      const addOns = this.user.user_entities.map(item => { return { ...item, selected: true } })
      
      this.$store.commit('users/SET_ADDONS', this.user.user_entities)
    }
    if(this.currentEntity){
      this.setCustomFields()
    }
  }
}
</script>

<style lang="scss" scoped>
.validating-manager {
  color: #85858e;
}

.add-establishment {
  background: #e51349;
  color: white;
  margin-bottom: 20px;
  &:hover {
    background: #e51349;
    color: white;
  }
}

.update-establishment {
  background: #e51349;
  color: white;
  margin-top: 20px;
  padding: 6px 35px;
  border-radius: 5px;
  &:hover {
    background: #e51349;
    color: white;
  }
}

.contract-information {
  background-color: #eeefef;
  border-radius: 8px;
}
.birthdate-picker {
  max-width: 25rem;
}

.validate {
  background: #e51349;
  border: #e51349;
  &:hover, &:focus {
    border: #e51349;
    background: #e51349;
    opacity: 0.5;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/users/UserForm.vue